import React, { Fragment, useState } from 'react';
import { Check } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { SlideLayout } from './SlideLayout';
type Pillar = {
  title: string;
  displayTitle: string;
  points: string[];
};
const pillars: Pillar[] = [{
  title: 'Sales Systems & Operations',
  displayTitle: 'Sales Systems\n& Operations',
  points: ['Sales enablement software', 'Sales training', 'Product-market fit', 'Compelling commissions']
}, {
  title: 'Sales Execution & Culture',
  displayTitle: 'Sales Execution\n& Culture',
  points: ['Daily and weekly meetings', 'Incentives and accountability', 'Training and one-on-ones', 'Team events']
}, {
  title: 'Growth Path & Leadership Development',
  displayTitle: 'Growth Path &\nLeadership Development',
  points: ['Advancement opportunity', 'Coaching', 'Leadership development', 'A future worth pursuing']
}, {
  title: 'Predictable Recruiting',
  displayTitle: 'Predictable\nRecruiting',
  points: ['Consistent volume of qualified reps, every week', 'Run like a business unit, not a side task', 'Sourced, screened, interviewed, and hired', 'Onboarded, integrated, trained, and launched with momentum']
}];
export function Slide1_Category() {
  const [activePillar, setActivePillar] = useState(pillars.length - 1);
  const selectedPillar = pillars[activePillar];
  return <SlideLayout className="text-center justify-start pt-28 sm:pt-32" data-id="element-120" data-mp="10yhe9uutmsyw">
      <div className="flex flex-col items-center w-full space-y-10 sm:space-y-12" data-id="element-121" data-mp="m91skta5ddq2">
        <div className="space-y-6 sm:space-y-8 max-w-7xl px-4" data-id="element-122" data-mp="r7minzvoqh0s">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-white leading-[1.1] whitespace-nowrap" data-id="element-123" data-mp="r7w9fpok0s7j">
            <span className="block" data-id="element-124" data-mp="h9yk0yuykpdc">D2D Growth Isn't Capped by Sales.</span>
            <span className="block text-[#7FE0C3]" data-id="element-125" data-mp="2jw3xhp18x6kz">It's Capped by Recruiting.</span>
          </h1>
          <p className="text-xl sm:text-2xl md:text-3xl text-slate-400 font-light max-w-3xl mx-auto" data-id="element-126" data-mp="2f5du2hcdrdsx">
            Every missed rep costs money, momentum, and morale.
          </p>
        </div>

        {/* Pillar Selector */}
        <div className="w-full max-w-7xl px-4 overflow-x-auto pt-4 sm:pt-8 pb-4 scrollbar-hide" data-id="element-127" data-mp="257pv1f7qrk4y">
          <div className="flex items-center justify-center min-w-[960px]" data-id="element-128" data-mp="32vhvqj60d3ha">
            {pillars.map((pillar, idx) => <Fragment key={pillar.title} data-id="element-129" data-mp="2xx6n2brg8z0n" data-mpi={idx}>
                {/* Chain Link */}
                <motion.button type="button" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2 + idx * 0.1
            }} onClick={() => setActivePillar(idx)} aria-pressed={activePillar === idx} className={`
                    relative flex items-center justify-center px-4 py-5 rounded-lg border-2 
                    w-52 h-28 shrink-0 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0F1C]
                    ${activePillar === idx ? 'bg-[#7FE0C3]/20 border-[#7FE0C3] text-[#7FE0C3] shadow-[0_0_30px_-5px_rgba(127,224,195,0.3)]' : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:border-slate-500'}
                  `} data-id="element-130" data-mp="2sw0t6am1z5pj">
                  <span className="text-sm sm:text-base font-bold text-center whitespace-pre-line leading-tight" data-id="element-131" data-mp="knccibz7eolv">
                    {pillar.displayTitle}
                  </span>
                </motion.button>

                {/* Connector (except after last item) */}
                {idx < pillars.length - 1 && <motion.div initial={{
              scaleX: 0,
              opacity: 0
            }} animate={{
              scaleX: 1,
              opacity: 1
            }} transition={{
              delay: 0.3 + idx * 0.1
            }} className="flex items-center -mx-1 z-10" data-id="element-132" data-mp="1w4x84g8voeel">
                    <div className="w-3 h-3 rounded-full bg-slate-600 shrink-0" data-id="element-133" data-mp="1df8jf4vmdu7o" />
                    <div className="w-8 h-1.5 bg-slate-700" data-id="element-134" data-mp="18hfc28ikyu5t" />
                    <div className="w-3 h-3 rounded-full bg-slate-600 shrink-0" data-id="element-135" data-mp="1n5a6pug5f8lj" />
                  </motion.div>}
              </Fragment>)}
          </div>
        </div>

        <div className="w-full max-w-4xl px-4" aria-live="polite" data-id="element-136" data-mp="unxrqli2ldeq">
          <AnimatePresence mode="wait" data-id="element-137" data-mp="um4jj1rt6l4m">
            <motion.section key={selectedPillar.title} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.22
          }} className="bg-[#7FE0C3]/5 border border-[#7FE0C3]/20 rounded-xl px-6 py-5 sm:px-8 sm:py-6 text-left" aria-labelledby="active-pillar-title" data-id="element-138" data-mp="pq625y4we3dp">
              <div className="flex items-center gap-3 mb-4" data-id="element-139" data-mp="2we6bvee9e7s9">
                <span className="h-px w-8 bg-[#7FE0C3]" data-id="element-140" data-mp="3k2pog64cd6q5" />
                <h2 id="active-pillar-title" className="text-sm sm:text-base font-bold uppercase tracking-[0.16em] text-[#7FE0C3]" data-id="element-141" data-mp="3k14og0z9cvkw">
                  {selectedPillar.title}
                </h2>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3" data-id="element-142" data-mp="3f2tgjvsiwv5b">
                {selectedPillar.points.map(point => <li key={point} className="flex items-start gap-2.5 text-sm sm:text-base text-slate-200 leading-snug" data-id="element-143" data-mp="1zpqnvmqss6h9">
                    <Check className="w-4 h-4 text-[#7FE0C3] mt-0.5 shrink-0" aria-hidden="true" data-id="element-144" data-mp="a5z3tymtityz" />
                    <span data-id="element-145" data-mp="57fio0g1tm7o">{point}</span>
                  </li>)}
              </ul>
            </motion.section>
          </AnimatePresence>
        </div>
      </div>
    </SlideLayout>;
}